import React from 'react';
import { useSelector } from 'react-redux';
import { Carousel, CarouselItem, Button } from '@americanexpress/dls-react';
import { getOneCmsLoungeFinderImagesBaseUrl } from '@/selectors/oneCmsSelectors';
import { useCenturionNetworkCarouselImages } from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import { useAnalyticsContext } from '@/context/analyticsContext';
import styles from './CenturionCarrouselSection.module.scss';

type CenturionCarouselImage = {
  image: string;
  imageAlt?: string;
  mobileImage?: string;
  mobileImageAlt?: string;
  cta?: string;
  url?: string;
};

const fallbackCarouselImages: CenturionCarouselImage[] = [
  {
    image: '1-SLC-Lounge-Image-desktop.png',
    imageAlt: 'The Centurion Lounge at Salt Lake City',
    mobileImage: '1-SLC-Lounge-Image-mobile.png',
    mobileImageAlt: 'The Centurion Lounge at Salt Lake City Mobile',
    cta: 'Explore Lounge',
    url: 'https://www.thecenturionlounge.com/locations/slc/',
  },
  {
    image: '2-JPN-Lounge-Image-desktop.png',
    imageAlt: 'The Centurion Lounge at Tokyo Haneda',
    mobileImage: '2-JPN-Lounge-Image-mobile.png',
    mobileImageAlt: 'The Centurion Lounge at Tokyo Haneda Mobile',
    cta: 'Explore Lounge',
    url: 'https://www.thecenturionlounge.com/locations/hnd/',
  },
  {
    image: '3-ATL-Lounge-Image-desktop.png',
    imageAlt: 'The Centurion Lounge at Atlanta',
    mobileImage: '3-ATL-Lounge-Image-mobile.png',
    mobileImageAlt: 'The Centurion Lounge at Atlanta Mobile',
    cta: 'Explore Lounge',
    url: 'https://www.thecenturionlounge.com/locations/atl/',
  },
];

export const CenturionCarrouselSection = () => {
  const baseOneCmsImgUrl = useSelector(getOneCmsLoungeFinderImagesBaseUrl);
  const cmsCarouselImages =
    useCenturionNetworkCarouselImages() as CenturionCarouselImage[];
  const { customTrack } = useAnalyticsContext();

  const trackCarouselCtaButton = (
    event: React.MouseEvent<HTMLElement>,
    loungeName: string,
    position: number
  ) => {
    customTrack({
      event,
      element: 'carousel',
      detail: `${loungeName}>>position ${position}>>Explore Lounge`,
    });
  };

  const carouselImages =
    cmsCarouselImages.length > 0 ? cmsCarouselImages : fallbackCarouselImages;

  return (
    <div className="tw-pt-10">
      <Carousel
        id="centurion-homepage-carousel"
        shouldAutoPlay={true}
        isSlideFullWidth={true}
        reactlytics={{ disable: true }}
      >
        {carouselImages.map(({ image, imageAlt, mobileImage, cta, url }, i) => {
          const imgUrl = `${baseOneCmsImgUrl}${image.replace(/^\//, '')}`;
          const mobileImgUrl = mobileImage
            ? `${baseOneCmsImgUrl}${mobileImage.replace(/^\//, '')}`
            : imgUrl;
          const loungeName =
            imageAlt ||
            `#${i + 1} of ${carouselImages.length} images of Centurion Lounge`;
          const ctaLabel = cta || 'Explore Lounge';

          return (
            <CarouselItem
              key={imgUrl}
              id={`centurion-homepage-carousel-slide-${i}`}
              style={{ borderRadius: 0 }}
            >
              <div className="tw-relative tw-overflow-hidden">
                <picture>
                  <source media="(max-width: 567px)" srcSet={mobileImgUrl} />
                  <img
                    src={imgUrl}
                    alt={
                      imageAlt ||
                      `#${i + 1} of ${carouselImages.length} images of Centurion Lounge`
                    }
                    className="tw-block tw-h-full tw-w-full tw-object-cover"
                    style={{ borderRadius: 0 }}
                  />
                </picture>
                {/* Blur + gradient layer */}
                <div
                  className={`${styles.carouselOverlay} tw-pointer-events-none tw-absolute tw-inset-x-0 tw-bottom-0 tw-z-10`}
                />
                {/* Content layer */}
                <div className="tw-absolute tw-inset-x-0 tw-bottom-0 tw-z-20">
                  <div className="tw-mx-auto tw-flex tw-w-full tw-max-w-[1440px] tw-flex-col tw-justify-center tw-gap-4 tw-px-4 tw-py-6 md_tw-flex-row md_tw-items-center md_tw-justify-around md_tw-gap-144">
                    <p className="tw-min-w-0 tw-whitespace-normal tw-text-center tw-font-sans tw-text-base tw-font-normal tw-text-gray-100 md_tw-whitespace-nowrap md_tw-text-left">
                      {imageAlt}
                    </p>
                    {url ? (
                      <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        tabIndex={-1}
                        className="tw-shrink-0"
                      >
                        <Button
                          reactlytics={{ disable: true }}
                          onClick={(event) =>
                            trackCarouselCtaButton(event, loungeName, i + 1)
                          }
                        >
                          {ctaLabel}
                        </Button>
                      </a>
                    ) : (
                      <Button
                        reactlytics={{ disable: true }}
                        onClick={(event) => {
                          trackCarouselCtaButton(event, loungeName, i + 1);
                          const searchBar =
                            document.querySelector<HTMLElement>('#findLounges');
                          if (searchBar) searchBar.focus();
                        }}
                      >
                        {ctaLabel}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </CarouselItem>
          );
        })}
      </Carousel>
    </div>
  );
};
